import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../helpers/api_helper';

// Types
export interface Market {
  id: number;
  pos: number;
  name: string;
  type: number;
  current_status: number;
  current_status_name: string;
  open_time: string;
  close_time: string;
  live_results: boolean;
  open_result_time: string;
  close_result_time: string;
  saturday_open: boolean;
  sunday_open: boolean;
  auto_results: boolean;
  is_active: boolean;
  manual_closed_date: string | null;
  timezone?: string;
  open_pana: string | null;
  open_digit: string | null;
  close_pana: string | null;
  close_digit: string | null;
  jodi_digit: string | null;
  created_at: string;
  updated_at: string;
}

export interface Pagination {
  current_page: number;
  total_pages: number;
  total_count: number;
  has_next: boolean;
  has_previous: boolean;
  page_size: number;
}

export interface Filters {
  search: string;
  type: string;
  status: string;
}

export interface MarketApiResponse {
  success: boolean;
  data: Market[];
  pagination: Pagination;
  filters: Filters;
}

export interface MarketState {
  markets: Market[];
  pagination: Pagination | null;
  filters: Filters;
  loading: boolean;
  error: string | null;
  success: string | null;
  activeCount: number;
  closedCount: number;
}

const initialState: MarketState = {
  markets: [],
  pagination: null,
  filters: {
    search: '',
    type: '',
    status: ''
  },
  loading: false,
  error: null,
  success: null,
  activeCount: 0,
  closedCount: 0,
};

// Async thunks
export const fetchMarkets = createAsyncThunk(
  'market/fetchMarkets',
  async (params: { page?: number; page_size?: number; search?: string; type?: string; status?: string } = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.page_size) {
        queryParams.append('page_size', params.page_size.toString());
      }
      if (params.search) queryParams.append('search', params.search);
      if (params.type) queryParams.append('type', params.type);
      if (params.status) queryParams.append('status', params.status);

      const url = `/admin/markets/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await api.get(url);
      console.log('Markets API Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Markets API Error:', error);
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const fetchMarketStats = createAsyncThunk(
  'market/fetchMarketStats',
  async (_, { rejectWithValue }) => {
    try {
      // Fetch all markets without pagination to get total counts
      const response = await api.get('/admin/markets/');
      console.log('Market Stats API Response:', response.data);
      
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        const allMarkets = response.data.data as Market[];
        const activeCount = allMarkets.filter((market: Market) => market.current_status_name === 'Running now').length;
        const closedCount = allMarkets.filter((market: Market) => market.current_status_name === 'Betting is closed').length;
        
        return {
          activeCount,
          closedCount,
          totalCount: allMarkets.length
        };
      }
      
      return {
        activeCount: 0,
        closedCount: 0,
        totalCount: 0
      };
    } catch (error: any) {
      console.error('Market Stats API Error:', error);
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export interface CreateMarketPayload {
  pos: number;
  name: string;
  type: number;
  current_status: number;
  open_time: string;
  close_time: string;
  live_results: boolean;
  open_result_time: string;
  close_result_time: string;
  saturday_open: boolean;
  sunday_open: boolean;
  auto_results: boolean;
  is_active: boolean;
  timezone: string;
}

export const createMarket = createAsyncThunk(
  'market/createMarket',
  async (data: CreateMarketPayload, { rejectWithValue }) => {
    try {
      const response = await api.post('/admin/markets/create', data);
      console.log('Create Market API Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Create Market API Error:', error);
      return rejectWithValue(
        error.response?.data?.error ||
        error.response?.data?.detail ||
        error.response?.data ||
        error.message
      );
    }
  }
);

export const updateMarket = createAsyncThunk(
  'market/updateMarket',
  async (data: { id: number; [key: string]: any }, { rejectWithValue }) => {
    try {
      const { id, ...updateData } = data;
      const response = await api.put(`/admin/markets/${id}/`, updateData);
      console.log('Update Market API Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Update Market API Error:', error);
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const patchMarket = createAsyncThunk(
  'market/patchMarket',
  async (data: { id: number; [key: string]: any }, { rejectWithValue }) => {
    try {
      const { id, ...patchData } = data;
      const response = await api.patch(`/admin/markets/${id}/`, patchData);
      console.log('Patch Market API Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Patch Market API Error:', error);
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const deleteMarket = createAsyncThunk(
  'market/deleteMarket',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/admin/markets/${id}/`);
      return { id };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

const marketSlice = createSlice({
  name: 'market',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    setFilters: (state, action: PayloadAction<Partial<Filters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch markets
    builder
      .addCase(fetchMarkets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMarkets.fulfilled, (state, action) => {
        state.loading = false;
        const response = action.payload as MarketApiResponse;
        state.markets = response.data || [];
        state.pagination = response.pagination || null;
        state.filters = response.filters || state.filters;
      })
      .addCase(fetchMarkets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch market stats
    builder
      .addCase(fetchMarketStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMarketStats.fulfilled, (state, action) => {
        state.loading = false;
        state.activeCount = action.payload.activeCount;
        state.closedCount = action.payload.closedCount;
      })
      .addCase(fetchMarketStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update market
    builder
      .addCase(updateMarket.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMarket.fulfilled, (state, action) => {
        state.loading = false;
        state.success = 'Market updated successfully';
        const updatedMarket = action.payload;
        const index = state.markets.findIndex(market => market.id === updatedMarket.id);
        if (index !== -1) {
          state.markets[index] = updatedMarket;
        }
      })
      .addCase(updateMarket.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Patch market
    builder
      .addCase(patchMarket.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(patchMarket.fulfilled, (state, action) => {
        state.loading = false;
        state.success = 'Market updated successfully';
        const updatedMarket = action.payload;
        const index = state.markets.findIndex(market => market.id === updatedMarket.id);
        if (index !== -1) {
          state.markets[index] = { ...state.markets[index], ...updatedMarket };
        }
      })
      .addCase(patchMarket.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create market
    builder
      .addCase(createMarket.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMarket.fulfilled, (state, action) => {
        state.loading = false;
        state.success = 'Market created successfully';
        state.markets.unshift(action.payload);
      })
      .addCase(createMarket.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.error =
          typeof payload === 'string'
            ? payload
            : payload
              ? JSON.stringify(payload)
              : 'Failed to create market';
      });

    // Delete market
    builder
      .addCase(deleteMarket.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMarket.fulfilled, (state, action) => {
        state.loading = false;
        state.success = 'Market deleted successfully';
        state.markets = state.markets.filter(market => market.id !== action.payload.id);
      })
      .addCase(deleteMarket.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearSuccess, setFilters, resetState } = marketSlice.actions;
export default marketSlice.reducer;
